import { useEffect, useState } from 'react';
import { LogOut, Pencil, Trash2, Loader2, Pin, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const TAGS = ['UPDATE', 'EVENT', 'MILESTONE', 'STORY'];

function detailFrom(data) {
  const d = data?.detail;
  if (d == null) return 'Something went wrong. Try again.';
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).join(' ');
  if (typeof d.msg === 'string') return d.msg;
  return String(d);
}

async function api(path, options = {}) {
  const res = await fetch(`${API}/api${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...options
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error(detailFrom(data));
    err.status = res.status;
    throw err;
  }
  return data;
}

const EMPTY_FORM = { title: '', tag: 'UPDATE', body: '', pinned: false };

export default function AdminPage() {
  const [me, setMe] = useState(null);
  const [posts, setPosts] = useState([]);
  const [creds, setCreds] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');

  const loadPosts = () =>
    api('/news')
      .then((d) => setPosts(Array.isArray(d) ? d : []))
      .catch(() => setPosts([]));

  useEffect(() => {
    document.title = 'ADMIN — ARC TOONS';
    api('/auth/me')
      .then((u) => {
        setMe(u);
        loadPosts();
      })
      .catch(() => setMe(false));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoginError('');
    setBusy(true);
    try {
      const u = await api('/auth/login', { method: 'POST', body: JSON.stringify(creds) });
      setMe(u);
      loadPosts();
    } catch (err) {
      setLoginError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await api('/auth/logout', { method: 'POST' }).catch(() => {});
    setMe(false);
    setCreds({ email: '', password: '' });
  };

  const publish = async (e) => {
    e.preventDefault();
    setBusy(true);
    setNotice('');
    try {
      if (editingId) {
        await api(`/news/${editingId}`, { method: 'PUT', body: JSON.stringify(form) });
        setNotice('Post updated.');
      } else {
        await api('/news', { method: 'POST', body: JSON.stringify(form) });
        setNotice('Post published to the News Board.');
      }
      setForm(EMPTY_FORM);
      setEditingId(null);
      loadPosts();
    } catch (err) {
      setNotice(err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      await api(`/news/${post.id}`, { method: 'DELETE' });
      if (editingId === post.id) {
        setEditingId(null);
        setForm(EMPTY_FORM);
      }
      loadPosts();
    } catch (err) {
      setNotice(err.message);
    }
  };

  const startEdit = (post) => {
    setEditingId(post.id);
    setForm({ title: post.title, tag: post.tag, body: post.body, pinned: post.pinned });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (me === null) {
    return (
      <div className="flex min-h-screen items-center justify-center pt-24" data-testid="admin-loading">
        <Loader2 className="h-8 w-8 animate-spin text-electric" />
      </div>
    );
  }

  if (!me) {
    return (
      <div className="relative flex min-h-screen items-center justify-center px-6 pb-24 pt-32">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-sm rounded-3xl border border-electric/25 bg-navy2/80 p-8"
          style={{ backdropFilter: 'blur(14px)', boxShadow: '0 0 50px rgba(0,200,255,0.12)' }}
        >
          <p className="font-mono text-[9px] uppercase tracking-[0.4em] text-electric">RESTRICTED AREA</p>
          <h1 className="mt-3 font-display text-2xl font-extrabold uppercase tracking-tight">ADMIN ACCESS</h1>
          <label className="mt-7 block font-mono text-[9px] uppercase tracking-[0.3em] text-snow/50">Email</label>
          <input
            type="email"
            required
            data-testid="admin-login-email"
            value={creds.email}
            onChange={(e) => setCreds({ ...creds, email: e.target.value })}
            className="mt-2 w-full rounded-xl border border-electric/25 bg-navy/60 px-4 py-3 text-sm text-snow outline-none focus:border-electric"
            placeholder="admin@arctoons.io"
          />
          <label className="mt-4 block font-mono text-[9px] uppercase tracking-[0.3em] text-snow/50">Password</label>
          <input
            type="password"
            required
            data-testid="admin-login-password"
            value={creds.password}
            onChange={(e) => setCreds({ ...creds, password: e.target.value })}
            className="mt-2 w-full rounded-xl border border-electric/25 bg-navy/60 px-4 py-3 text-sm text-snow outline-none focus:border-electric"
            placeholder="••••••••"
          />
          {loginError && (
            <p className="mt-4 rounded-xl border border-ember/40 bg-ember/10 p-3 text-xs text-ember" data-testid="admin-login-error">
              {loginError}
            </p>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="light-sweep mt-6 w-full rounded-full bg-gradient-to-r from-arc via-electric to-arc py-3.5 font-display text-xs font-bold uppercase tracking-[0.2em] text-white shadow-[0_0_28px_rgba(0,200,255,0.4)] transition-transform duration-200 hover:scale-[1.02] disabled:opacity-50"
          >
            {busy ? 'CHECKING...' : 'ENTER ADMIN'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen pb-28 pt-32" data-testid="admin-panel">
      <div className="mx-auto max-w-[900px] px-6 sm:px-10">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.4em] text-electric">NEWS BOARD CONTROL</p>
            <h1 className="mt-3 font-display text-2xl font-extrabold uppercase tracking-tight sm:text-3xl">NEWS ADMIN</h1>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="flex items-center gap-2 rounded-full border border-electric/25 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-snow/70 transition-colors hover:border-ember hover:text-ember"
          >
            <LogOut className="h-3.5 w-3.5" /> LOGOUT
          </button>
        </div>

        <form
          onSubmit={publish}
          data-testid="admin-post-form"
          className="mt-8 rounded-3xl border border-electric/20 bg-navy2/70 p-6 sm:p-8"
          style={{ backdropFilter: 'blur(10px)' }}
        >
          <div className="flex items-center justify-between gap-3">
            <p className="font-display text-sm font-bold uppercase tracking-[0.2em] text-snow">
              {editingId ? 'EDIT TRANSMISSION' : 'NEW TRANSMISSION'}
            </p>
            {editingId && (
              <button
                type="button"
                onClick={() => { setEditingId(null); setForm(EMPTY_FORM); }}
                data-testid="admin-post-cancel"
                className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-snow/50 hover:text-snow"
              >
                <X className="h-3 w-3" /> CANCEL EDIT
              </button>
            )}
          </div>
          <label className="mt-5 block font-mono text-[9px] uppercase tracking-[0.3em] text-snow/50">Title</label>
          <input
            required
            minLength={3}
            maxLength={140}
            data-testid="admin-post-title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="mt-2 w-full rounded-xl border border-electric/25 bg-navy/60 px-4 py-3 text-sm text-snow outline-none focus:border-electric"
            placeholder="WHAT IS HAPPENING IN THE TOONVERSE?"
          />
          <div className="mt-4 flex flex-wrap items-center gap-6">
            <div>
              <label className="block font-mono text-[9px] uppercase tracking-[0.3em] text-snow/50">Tag</label>
              <select
                data-testid="admin-post-tag"
                value={form.tag}
                onChange={(e) => setForm({ ...form, tag: e.target.value })}
                className="mt-2 rounded-xl border border-electric/25 bg-navy/60 px-4 py-2.5 font-mono text-xs uppercase tracking-[0.2em] text-snow outline-none focus:border-electric"
              >
                {TAGS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <label className="mt-5 flex cursor-pointer items-center gap-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-snow/70">
              <input
                type="checkbox"
                data-testid="admin-post-pinned"
                checked={form.pinned}
                onChange={(e) => setForm({ ...form, pinned: e.target.checked })}
                className="h-4 w-4 accent-[#FFB52E]"
              />
              <Pin className="h-3.5 w-3.5 text-gold" /> PIN TO TOP
            </label>
          </div>
          <label className="mt-4 block font-mono text-[9px] uppercase tracking-[0.3em] text-snow/50">Body</label>
          <textarea
            required
            minLength={3}
            maxLength={5000}
            rows={6}
            data-testid="admin-post-body"
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            className="mt-2 w-full rounded-xl border border-electric/25 bg-navy/60 px-4 py-3 text-sm leading-relaxed text-snow outline-none focus:border-electric"
            placeholder="Write the update... line breaks are kept."
          />
          {notice && (
            <p className="mt-4 rounded-xl border border-electric/30 bg-electric/10 p-3 text-xs text-electric" data-testid="admin-notice">
              {notice}
            </p>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-post-submit"
            className="light-sweep mt-6 rounded-full bg-gradient-to-r from-gold to-ember px-10 py-3.5 font-display text-xs font-bold uppercase tracking-[0.2em] text-navy shadow-[0_0_28px_rgba(255,181,46,0.45)] transition-transform duration-200 hover:scale-[1.03] disabled:opacity-50"
          >
            {busy ? 'SENDING...' : editingId ? 'SAVE CHANGES' : 'PUBLISH'}
          </button>
        </form>

        <div className="mt-10 flex flex-col gap-4" data-testid="admin-post-list">
          {posts.length === 0 && (
            <p className="rounded-2xl border border-dashed border-electric/20 p-8 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-snow/40">
              NO POSTS YET — PUBLISH THE FIRST TRANSMISSION
            </p>
          )}
          {posts.map((post, i) => (
            <div
              key={post.id}
              data-testid={`admin-post-item-${i}`}
              className="flex items-start justify-between gap-4 rounded-2xl border border-electric/15 bg-navy2/60 p-5"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full border border-electric/30 px-2.5 py-0.5 font-mono text-[8px] uppercase tracking-[0.25em] text-electric">
                    {post.tag}
                  </span>
                  {post.pinned && <Pin className="h-3 w-3 text-gold" />}
                  <span className="font-mono text-[9px] text-snow/35">
                    {new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>
                <p className="mt-2 truncate font-display text-sm font-bold uppercase tracking-tight text-snow">{post.title}</p>
              </div>
              <div className="flex shrink-0 gap-2">
                <button
                  onClick={() => startEdit(post)}
                  data-testid={`admin-post-edit-${i}`}
                  aria-label={`Edit ${post.title}`}
                  className="flex h-9 w-9 items-center justify-center rounded-xl border border-electric/25 text-electric transition-colors hover:border-electric"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => remove(post)}
                  data-testid={`admin-post-delete-${i}`}
                  aria-label={`Delete ${post.title}`}
                  className="flex h-9 w-9 items-center justify-center rounded-xl border border-ember/30 text-ember transition-colors hover:border-ember hover:shadow-[0_0_16px_rgba(255,122,24,0.4)]"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
